import { useCallback, useLayoutEffect, useRef } from "react";

const MAX_STORED_POSITIONS = 100;

/** Routeradapter liefern nur den Historieneintrag; Query-/Filterwerte gehören nicht zum Seitenpfad. */
export type AppShellContentLocation = { key: string; pathname: string; action: "POP" | "PUSH" | "REPLACE" };
type Position = { main: number; content: number };

export function useAppShellScrollRestoration(location?: AppShellContentLocation) {
  const mainRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const positions = useRef(new Map<string, Position>());
  const current = useRef<{ key: string; pathname: string; position: Position } | null>(null);
  const restoring = useRef(false);
  const onScroll = useCallback(() => {
    if (restoring.current || !current.current) return;
    current.current.position = { main: mainRef.current?.scrollTop ?? 0, content: contentRef.current?.scrollTop ?? 0 };
  }, []);

  const key = location?.key;
  const pathname = location?.pathname;
  const action = location?.action;
  useLayoutEffect(() => {
    const main = mainRef.current;
    const content = contentRef.current;
    if (key === undefined || pathname === undefined || !main || !content) return;
    const previous = current.current;
    if (previous) positions.current.set(previous.key, { ...previous.position });
    // Filter-/Präferenz-Replaces auf derselben Seite dürfen die Liste nicht nach oben versetzen.
    const target = previous?.pathname === pathname ? { ...previous.position }
      : action === "POP" ? positions.current.get(key) ?? { main: 0, content: 0 }
      : { main: 0, content: 0 };
    current.current = { key, pathname, position: { ...target } };
    if (positions.current.size > MAX_STORED_POSITIONS) positions.current.delete(positions.current.keys().next().value!);
    restoring.current = true;
    let frame: number | null = null;
    const observer = new ResizeObserver(() => schedule());
    const mutations = new MutationObserver(() => schedule());
    const stop = () => {
      restoring.current = false;
      observer.disconnect();
      mutations.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };
    const restore = () => {
      frame = null;
      main.scrollTop = target.main;
      content.scrollTop = target.content;
      if (Math.abs(main.scrollTop - target.main) < 1 && Math.abs(content.scrollTop - target.content) < 1) stop();
    };
    function schedule() {
      if (restoring.current && frame === null) frame = requestAnimationFrame(restore);
    }
    const cancel = () => { stop(); onScroll(); };
    // Bei Zurück können Suspense und Datenabruf die Seite zunächst verkürzen. Bis zur passenden Höhe
    // erneut ausrichten, aber jede bewusste Nutzerinteraktion beendet die Wiederherstellung sofort.
    observer.observe(main);
    observer.observe(content);
    mutations.observe(content, { childList: true, subtree: true });
    main.addEventListener("wheel", cancel, { passive: true });
    main.addEventListener("touchstart", cancel, { passive: true });
    main.addEventListener("pointerdown", cancel, { passive: true });
    main.addEventListener("keydown", cancel);
    restore();
    return () => {
      stop();
      main.removeEventListener("wheel", cancel);
      main.removeEventListener("touchstart", cancel);
      main.removeEventListener("pointerdown", cancel);
      main.removeEventListener("keydown", cancel);
    };
  }, [key, pathname, action, onScroll]);
  return { mainRef, contentRef, onScroll };
}
